import { Link } from '@tanstack/react-router'
import { UserRound } from 'lucide-react'

import { GitHubConnection } from '@/components/settings/GitHubConnection'
import { RiskBadge } from '@/components/ui/Badge'
import { PageHeader, Panel } from '@/components/ui/Panel'
import { QueryBoundary, Skeleton, TableSkeleton } from '@/components/ui/States'
import { useAuth } from '@/features/auth/useAuth'
import { useSystemStatus, useToolCatalog } from '@/features/system/useSystemStatus'
import { env } from '@/config/env'
import { formatRelative } from '@/lib/utils/format'

function Row({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div
      className="grid grid-cols-1 gap-1 border-b border-line/60 px-4 py-2.5 last:border-b-0
        sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-4"
    >
      <dt className="text-xs text-ink-muted" title={hint}>
        {label}
      </dt>
      <dd className="min-w-0 text-sm text-ink-dim">{value}</dd>
    </div>
  )
}

export function SettingsPage() {
  const status = useSystemStatus()
  const catalog = useToolCatalog()
  const { user } = useAuth()

  return (
    <div>
      <PageHeader
        title="Settings"
        description="Manage your GitHub connection and account, and review how this server is configured. Your GitHub token is stored encrypted and is never displayed."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <GitHubConnection />

        <Panel
          title="Your account"
          actions={
            <Link
              to="/profile"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line-strong
                bg-surface px-2.5 text-xs font-medium text-ink shadow-card transition-colors
                hover:bg-hover"
            >
              <UserRound aria-hidden="true" className="h-3.5 w-3.5" />
              Edit profile
            </Link>
          }
        >
          <dl>
            <Row label="Signed in as" value={<span className="mono">{user?.email ?? '—'}</span>} />
            <Row label="Display name" value={user?.display_name ?? '—'} />
            <Row
              label="Session"
              value="Held in an httpOnly cookie"
              hint="No token is readable by JavaScript on this page, so an XSS cannot steal your session."
            />
            <Row
              label="Isolation"
              value="Your repositories, history and approvals are visible only to you"
              hint="Enforced on the server for every request, not by this page."
            />
          </dl>
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="GitHub service health">
          <QueryBoundary
            isLoading={status.isLoading}
            error={status.error}
            onRetry={() => void status.refetch()}
            loadingFallback={<Skeleton className="m-4 h-32" />}
          >
            <dl>
              <Row
                label="Reachable"
                value={
                  <span
                    className={
                      status.data?.github.state === 'healthy' ? 'text-ok' : 'text-waiting'
                    }
                  >
                    {status.data?.github.state === 'healthy'
                      ? 'Yes — your credential answered'
                      : (status.data?.github.detail ?? status.data?.github.state)}
                  </span>
                }
                hint="Probed with your own credential, so the rate limit below is yours."
              />
              <Row
                label="Rate limit"
                value={
                  status.data?.github.rate_limit_remaining != null ? (
                    <span className="mono">
                      {status.data.github.rate_limit_remaining} / {status.data.github.rate_limit_limit}{' '}
                      remaining
                    </span>
                  ) : (
                    '—'
                  )
                }
              />
              <Row
                label="Resets"
                value={formatRelative(status.data?.github.rate_limit_reset_at)}
              />
            </dl>
          </QueryBoundary>
        </Panel>

        <Panel title="Safety">
          <QueryBoundary
            isLoading={status.isLoading}
            error={status.error}
            onRetry={() => void status.refetch()}
            loadingFallback={<Skeleton className="m-4 h-32" />}
          >
            <dl>
              <Row
                label="Read-only mode"
                value={
                  status.data?.read_only_mode ? (
                    <span className="text-denied">On — every write tool is refused</span>
                  ) : (
                    'Off'
                  )
                }
                hint="A single environment variable that turns the whole deployment read-only."
              />
              <Row
                label="MCP endpoint"
                value={
                  status.data?.mcp.detail?.includes('unauthenticated') ? (
                    <span className="text-waiting">Unauthenticated</span>
                  ) : (
                    'Bearer token required'
                  )
                }
              />
            </dl>
          </QueryBoundary>
        </Panel>
      </div>

      <Panel
        title="Tool catalog"
        description="What the model is offered, and what each tool is allowed to do"
        className="mt-4"
      >
        <QueryBoundary
          isLoading={catalog.isLoading}
          error={catalog.error}
          onRetry={() => void catalog.refetch()}
          loadingFallback={<TableSkeleton rows={8} columns={3} />}
        >
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col" className="w-64">
                    Tool
                  </th>
                  <th scope="col" className="w-24">
                    Risk
                  </th>
                  <th scope="col">What it does</th>
                </tr>
              </thead>
              <tbody>
                {(catalog.data?.tools ?? []).map((tool) => (
                  <tr key={tool.name}>
                    <td className="mono text-ink">{tool.name}</td>
                    <td>
                      <RiskBadge risk={tool.risk} />
                    </td>
                    <td className="text-xs text-ink-muted">
                      {tool.description.split('\n')[0]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </QueryBoundary>
      </Panel>

      <Panel title="Application" className="mt-4">
        <dl>
          <Row label="Environment" value={<span className="mono">{env.appEnv}</span>} />
          <Row label="Backend version" value={<span className="mono">{status.data?.version ?? '—'}</span>} />
          <Row
            label="API base"
            value={<span className="mono">{env.apiBaseUrl || 'same origin'}</span>}
          />
          <Row label="Tools registered" value={<span className="mono">{status.data?.tool_count ?? '—'}</span>} />
        </dl>
      </Panel>
    </div>
  )
}
